import React, { useCallback, useRef, useState } from 'react'

import { Box, DisplayPosition, Overflow, ZIndex } from '@gousto-internal/citrus-react'

import { Collapsed } from './components/Collapsed'
import { EditBoxSizeModal } from './components/EditBoxSizeModal'
import { EditDeliveryAddressModal } from './components/EditDeliveryAddressModal'
import { Expanded } from './components/Expanded'
import {
  trackEditOrderDetailsAddress,
  trackEditOrderDetailsBoxSize,
  trackOrderDetailsDropdownOpened,
} from './tracking'
import { useCollapseOnScroll } from './useCollapseOnScroll'
import { useDeliverySettings } from './useDeliverySettings'
import { useDiscountBadge } from './useDiscountBadge'
import { useDismissOnOutside } from './useDismissOnOutside'
import { useHeightCollapseTransition } from './useHeightCollapseTransition'
import { useOrderDetailsTrackingProperties } from './useOrderDetailsTrackingProperties'
import { usePublishCssVarHeight } from './usePublishCssVarHeight'

type PanelState = {
  isExpanded: boolean
  // Keeps the panel's content mounted through its collapse transition before unmounting.
  isClosing: boolean
  isOverlayMode: boolean
}

// Expanded by default on landing; the first collapse — however it's triggered —
// permanently switches the dropdown into overlay mode for the session.
const INITIAL_PANEL_STATE: PanelState = {
  isExpanded: true,
  isClosing: false,
  isOverlayMode: false,
}

// TODO: Split this component into a dumb presenter that takes data via props.
// The eligibility + data fetch (useDeliverySettings) should live in the parent
// (MenuPage), which then decides whether to mount the dropdown. Today the
// component owns its own visibility and data wiring, which couples presentation
// to Redux/SWR, complicates tests, and blocks reuse outside MenuPage.
export const DeliverySettingsDropdown = () => {
  const { isEnabled, boxSize, deliveryDayShort, deliveryDayLong, postcode, shortAddress } =
    useDeliverySettings()
  const trackingProperties = useOrderDetailsTrackingProperties()
  const discountBadge = useDiscountBadge({ enabled: isEnabled })
  // One state object so every transition is a single atomic update — close() runs from
  // native listeners (scroll, useDismissOnOutside) that React 17 doesn't batch, and
  // separate setState calls there could unmount the content before it gets to animate.
  const [panelState, setPanelState] = useState<PanelState>(INITIAL_PANEL_STATE)
  const { isExpanded, isClosing, isOverlayMode } = panelState
  const [isEditBoxSizeModalOpen, setIsEditBoxSizeModalOpen] = useState(false)
  const [isEditDeliveryAddressModalOpen, setIsEditDeliveryAddressModalOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const expandedContentRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    // isOverlayMode flips later, in onTransitionEnd — doing it here would switch to
    // position: absolute before the panel's finished shrinking, popping it out of flow.
    setPanelState((prev) => ({ ...prev, isExpanded: false, isClosing: true }))
  }, [])

  const toggle = () => {
    if (isExpanded) {
      close()
    } else {
      trackOrderDetailsDropdownOpened(trackingProperties)
      setPanelState((prev) => ({ ...prev, isExpanded: true, isClosing: false }))
    }
  }

  const handleEditBoxSize = () => {
    trackEditOrderDetailsBoxSize()
    setIsEditBoxSizeModalOpen(true)
  }

  const handleEditAddress = () => {
    trackEditOrderDetailsAddress()
    setIsEditDeliveryAddressModalOpen(true)
  }

  const isAnyModalOpen = isEditBoxSizeModalOpen || isEditDeliveryAddressModalOpen
  // isEnabled gates both: eligibility resolves asynchronously, so without it a stray
  // scroll/outside-click before that resolves could close() the panel pre-emptively.
  useDismissOnOutside(wrapperRef, isEnabled && isExpanded && !isAnyModalOpen, close)
  // First scroll while in push mode collapses the dropdown into overlay mode.
  useCollapseOnScroll(isEnabled && isExpanded && !isOverlayMode && !isAnyModalOpen, close)
  // Consumed by MenuPage's sticky NavBar to offset its top.
  usePublishCssVarHeight(wrapperRef, '--delivery-settings-dropdown-height', isEnabled)
  const collapseTransition = useHeightCollapseTransition(expandedContentRef, isExpanded)

  if (!isEnabled) return null

  const layoutMode = isOverlayMode ? 'overlay' : 'push'

  return (
    // Sticky even in push mode — otherwise native scroll carries the panel away before
    // React re-renders it into overlay mode, so the collapse never gets to animate.
    <Box ref={wrapperRef} zIndex={ZIndex.Dropdown} top="0" style={{ position: 'sticky' }}>
      <Collapsed
        boxSize={boxSize}
        deliveryDay={deliveryDayShort}
        address={postcode}
        isExpanded={isExpanded}
        onEditClick={toggle}
        discountText={discountBadge?.collapsedText}
      />
      {/* Never unmounted — losing the ref would break height measurement on reopen. */}
      <Box
        ref={expandedContentRef}
        position={isOverlayMode ? DisplayPosition.Absolute : DisplayPosition.Relative}
        top={isOverlayMode ? '100%' : undefined}
        left={isOverlayMode ? '0' : undefined}
        right={isOverlayMode ? '0' : undefined}
        overflowY={Overflow.Hidden}
        data-testid="delivery-settings-expanded-panel"
        data-layout-mode={layoutMode}
        // Only needed in overlay mode, where the panel floats over page content below it.
        boxShadow={isOverlayMode ? '0px 12px 24px rgba(0, 0, 0, 0.18)' : undefined}
        style={collapseTransition}
        onTransitionEnd={() => {
          setPanelState((prev) =>
            prev.isExpanded ? prev : { ...prev, isClosing: false, isOverlayMode: true },
          )
        }}
      >
        {(isExpanded || isClosing) && (
          <Expanded
            boxSize={boxSize}
            deliveryDay={deliveryDayLong}
            address={shortAddress}
            onEditBoxSize={handleEditBoxSize}
            onEditAddress={handleEditAddress}
            onRowConfirmed={close}
            onClose={close}
            discountText={discountBadge?.expandedText}
          />
        )}
      </Box>
      {isEditBoxSizeModalOpen && (
        <EditBoxSizeModal
          isOpen
          onClose={() => setIsEditBoxSizeModalOpen(false)}
          onConfirmComplete={() => {
            setIsEditBoxSizeModalOpen(false)
            close()
          }}
        />
      )}
      {isEditDeliveryAddressModalOpen && (
        <EditDeliveryAddressModal
          isOpen
          onClose={() => setIsEditDeliveryAddressModalOpen(false)}
          onConfirmComplete={() => {
            setIsEditDeliveryAddressModalOpen(false)
            close()
          }}
        />
      )}
    </Box>
  )
}
